import { useEffect, useRef, useState } from "react";
import type { DragEvent, ReactNode } from "react";
import type { Editor } from "@tiptap/core";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { ArrowDown, ArrowUp, Copy, GripVertical, Trash2 } from "lucide-react";

import { cn } from "@/lib/utils";

interface HandleTarget {
	index: number;
	top: number;
	left: number;
}

function topBlocks(editor: Editor) {
	const elements = [...editor.view.dom.children].filter(
		(element) =>
			!element.classList.contains("page-gap") &&
			!element.classList.contains("ProseMirror-gapcursor") &&
			!element.classList.contains("ProseMirror-widget"),
	);

	if (elements.length !== editor.state.doc.childCount) {
		return null;
	}

	return elements;
}

export function BlockHandle({ editor }: { editor: Editor }) {
	const [target, setTarget] = useState<HandleTarget | null>(null);
	const [menuOpen, setMenuOpen] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	const dragIndex = useRef<number | null>(null);
	const frozen = useRef(false);
	frozen.current = menuOpen;

	useEffect(() => {
		const key = new PluginKey("blockHandleDrop");

		editor.registerPlugin(
			new Plugin({
				key,
				props: {
					handleDrop: (view, event, slice, moved) => {
						const source = dragIndex.current;

						if (source === null) {
							if (!moved || slice.content.childCount !== 1) {
								return false;
							}

							return slice.content.firstChild?.type.name === "pageBreak";
						}

						dragIndex.current = null;

						const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
						const { doc, tr } = view.state;

						if (coords === null || source >= doc.childCount) {
							return true;
						}

						let insert = doc.resolve(coords.pos).index(0);

						if (insert < doc.childCount) {
							const dom = view.nodeDOM(doc.resolve(0).posAtIndex(insert));

							if (dom instanceof HTMLElement) {
								const rect = dom.getBoundingClientRect();

								if (event.clientY > rect.top + rect.height / 2) {
									insert += 1;
								}
							}
						}

						if (insert === source || insert === source + 1) {
							return true;
						}

						const node = doc.child(source);
						const from = doc.resolve(0).posAtIndex(source);

						tr.delete(from, from + node.nodeSize);

						const pos = tr.doc.resolve(0).posAtIndex(insert > source ? insert - 1 : insert);

						tr.insert(pos, node);
						tr.setSelection(NodeSelection.create(tr.doc, pos));
						view.dispatch(tr.scrollIntoView());

						return true;
					},
				},
			}),
		);

		return () => {
			editor.unregisterPlugin(key);
		};
	}, [editor]);

	useEffect(() => {
		const container = editor.view.dom.closest(".doc-block-gutter");

		if (!(container instanceof HTMLElement)) {
			return;
		}

		const handleMove = (event: MouseEvent) => {
			if (frozen.current) {
				return;
			}

			const blocks = topBlocks(editor);

			if (blocks === null) {
				setTarget(null);

				return;
			}

			const index = blocks.findIndex((element) => {
				const rect = element.getBoundingClientRect();

				return event.clientY >= rect.top && event.clientY <= rect.bottom;
			});

			if (index === -1) {
				return;
			}

			const containerRect = container.getBoundingClientRect();
			const contentLeft = editor.view.dom.getBoundingClientRect().left;

			setTarget({
				index,
				top: blocks[index].getBoundingClientRect().top - containerRect.top,
				left: Math.max(2, contentLeft - containerRect.left - 30),
			});
		};

		const handleLeave = () => {
			if (!frozen.current) {
				setTarget(null);
			}
		};

		container.addEventListener("mousemove", handleMove);
		container.addEventListener("mouseleave", handleLeave);

		return () => {
			container.removeEventListener("mousemove", handleMove);
			container.removeEventListener("mouseleave", handleLeave);
		};
	}, [editor]);

	useEffect(() => {
		if (!menuOpen) {
			return;
		}

		const handlePointerDown = (event: PointerEvent) => {
			if (root.current !== null && !root.current.contains(event.target as Node)) {
				setMenuOpen(false);
			}
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setMenuOpen(false);
			}
		};

		document.addEventListener("pointerdown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("pointerdown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [menuOpen]);

	if (target === null) {
		return null;
	}

	const node =
		target.index < editor.state.doc.childCount ? editor.state.doc.child(target.index) : null;

	if (node === null) {
		return null;
	}

	const pageBreak = node.type.name === "pageBreak";

	const stale = () => {
		if (target.index < editor.state.doc.childCount) {
			return false;
		}

		setMenuOpen(false);
		setTarget(null);

		return true;
	};

	const selectBlock = (index: number) => {
		const pos = editor.state.doc.resolve(0).posAtIndex(index);

		editor.view.dispatch(editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, pos)));

		return pos;
	};

	const handleDragStart = (event: DragEvent) => {
		if (stale()) {
			event.preventDefault();

			return;
		}

		const pos = selectBlock(target.index);
		const slice = editor.view.state.selection.content();
		const serialized = editor.view.serializeForClipboard(slice);

		event.dataTransfer.setData("text/html", serialized.dom.innerHTML);
		event.dataTransfer.setData("text/plain", serialized.text);
		event.dataTransfer.effectAllowed = "copyMove";

		const dom = editor.view.nodeDOM(pos);

		if (dom instanceof HTMLElement) {
			event.dataTransfer.setDragImage(dom, 0, 0);
		}

		editor.view.dragging = { slice, move: true };
		dragIndex.current = target.index;
		setMenuOpen(false);
	};

	const handleDragEnd = () => {
		dragIndex.current = null;
		editor.view.dragging = null;
		setTarget(null);
	};

	const run = (mutate: (index: number) => void) => {
		if (stale()) {
			return;
		}

		mutate(target.index);
		setMenuOpen(false);
		setTarget(null);
		editor.view.focus();
	};

	const move = (index: number, to: number) => {
		const node = editor.state.doc.child(index);
		const from = editor.state.doc.resolve(0).posAtIndex(index);
		const tr = editor.state.tr.delete(from, from + node.nodeSize);

		tr.insert(tr.doc.resolve(0).posAtIndex(to), node);
		editor.view.dispatch(tr.scrollIntoView());
	};

	const duplicate = (index: number) => {
		const node = editor.state.doc.child(index);
		const after = editor.state.doc.resolve(0).posAtIndex(index) + node.nodeSize;

		editor.view.dispatch(editor.state.tr.insert(after, node));
	};

	const remove = (index: number) => {
		const node = editor.state.doc.child(index);
		const from = editor.state.doc.resolve(0).posAtIndex(index);

		editor.view.dispatch(editor.state.tr.delete(from, from + node.nodeSize));
	};

	return (
		<div
			ref={root}
			data-component="block-handle"
			className="absolute z-30"
			style={{ top: target.top, left: target.left }}
		>
			<button
				type="button"
				aria-label="Mover ou editar bloco"
				draggable={!pageBreak}
				onDragStart={pageBreak ? undefined : handleDragStart}
				onDragEnd={pageBreak ? undefined : handleDragEnd}
				onClick={() => {
					if (stale()) {
						return;
					}

					selectBlock(target.index);
					setMenuOpen((current) => !current);
				}}
				className={cn(
					"flex h-6 w-5 items-center justify-center rounded-md text-foreground/30 transition-colors hover:bg-foreground/5 hover:text-foreground/70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
					!pageBreak && "cursor-grab active:cursor-grabbing",
				)}
			>
				<GripVertical className="size-4" />
			</button>

			{menuOpen && (
				<div className="absolute left-0 top-7 flex min-w-44 flex-col surface-panel p-1 font-sans text-foreground shadow-card">
					<BlockMenuItem
						label="Mover para cima"
						icon={<ArrowUp />}
						disabled={target.index === 0}
						onClick={() => run((index) => move(index, index - 1))}
					/>
					<BlockMenuItem
						label="Mover para baixo"
						icon={<ArrowDown />}
						disabled={target.index === editor.state.doc.childCount - 1}
						onClick={() => run((index) => move(index, index + 1))}
					/>
					{!pageBreak && (
						<BlockMenuItem label="Duplicar" icon={<Copy />} onClick={() => run(duplicate)} />
					)}
					<BlockMenuItem label="Apagar" icon={<Trash2 />} destructive onClick={() => run(remove)} />
				</div>
			)}
		</div>
	);
}

interface BlockMenuItemProps {
	label: string;
	icon: ReactNode;
	disabled?: boolean;
	destructive?: boolean;
	onClick(): void;
}

function BlockMenuItem({
	label,
	icon,
	disabled = false,
	destructive = false,
	onClick,
}: BlockMenuItemProps) {
	return (
		<button
			type="button"
			disabled={disabled}
			onClick={onClick}
			className={cn(
				"flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-4",
				destructive && "text-destructive hover:bg-destructive/10",
			)}
		>
			{icon}
			{label}
		</button>
	);
}
